import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import BiotechIcon from '@mui/icons-material/Biotech';
import ScienceIcon from '@mui/icons-material/Science';
import { IconButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { labReportService, tssService, tsService, vsService, substrateTsService, substrateVsService, nitrateNService, phosphateService } from '../services/api';
import { MonthlyLabReport, WeeklyLabRow, LabReportEntry, LabReportSection, TssDailyReport, TsDailyReport, VsDailyReport, SubstrateTsDailyReport, SubstrateVsDailyReport, NitrateNDailyReport, PhosphateDailyReport } from '../types';

// ─── Constants ────────────────────────────────────────────────────────────────

const SECTIONS: LabReportSection[] = ['F1', 'F2', 'F3', 'IST', 'MP'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// Columns for F1/F2/F3/IST sections
const STANDARD_COLS: { key: keyof LabReportEntry; label: string; unit: string }[] = [
  { key: 'tssPpm',               label: 'TSS',                  unit: 'ppm' },
  { key: 'tsValue',              label: 'TS',                   unit: 'ppm' },
  { key: 'vsValue',              label: 'VS',                   unit: 'ppm' },
  { key: 'nitrateNPpm',          label: 'Nitrate-N',            unit: 'ppm' },
  { key: 'phosphorusReactivePpm', label: 'Phosphorus Reactive', unit: 'ppm' },
  { key: 'phosphorusTotalPpm',   label: 'Phosphorus Total',     unit: 'ppm' },
];

// Columns for MP section (TS and VS are %; TSS stays ppm)
const MP_COLS: { key: keyof LabReportEntry; label: string; unit: string }[] = [
  { key: 'tsValue',              label: 'TS',                   unit: '%' },
  { key: 'vsValue',              label: 'VS',                   unit: '%' },
  { key: 'tssPpm',               label: 'TSS',                  unit: 'ppm' },
  { key: 'nitrateNPpm',          label: 'Nitrate-N',            unit: 'ppm' },
  { key: 'phosphorusReactivePpm', label: 'Phosphorus Reactive', unit: 'ppm' },
  { key: 'phosphorusTotalPpm',   label: 'Phosphorus Total',     unit: 'ppm' },
];

// ─── Colour palette ────────────────────────────────────────────────────────────

const SECTION_COLORS: Record<LabReportSection, { header: string; subheader: string; cellBg: string }> = {
  F1:  { header: '#1b5e20', subheader: '#2e7d32', cellBg: '#f1f8f1' },
  F2:  { header: '#1565c0', subheader: '#1976d2', cellBg: '#e8f0fd' },
  F3:  { header: '#4a148c', subheader: '#6a1b9a', cellBg: '#f3e5f5' },
  IST: { header: '#e65100', subheader: '#f57c00', cellBg: '#fff3e0' },
  MP:  { header: '#880e4f', subheader: '#ad1457', cellBg: '#fce4ec' },
};

// ─── Helper ────────────────────────────────────────────────────────────────────

const fmt = (v: number | null | undefined): string =>
  v == null ? '—' : v.toLocaleString(undefined, { maximumFractionDigits: 2 });

const colsFor = (section: LabReportSection) =>
  section === 'MP' ? MP_COLS : STANDARD_COLS;

// ─── Component ─────────────────────────────────────────────────────────────────

const ChemicalLabReport: React.FC = () => {
  const now = new Date();
  const navigate = useNavigate();
  const [year, setYear]     = useState<number>(now.getFullYear());
  const [month, setMonth]   = useState<number>(now.getMonth() + 1);
  const [tab, setTab]       = useState<number>(0);
  const [report, setReport] = useState<MonthlyLabReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState<string | null>(null);
  const [tssReports, setTssReports] = useState<TssDailyReport[]>([]);
  const [tsReports,  setTsReports]  = useState<TsDailyReport[]>([]);
  const [vsReports,  setVsReports]  = useState<VsDailyReport[]>([]);
  const [substrateTsReports, setSubstrateTsReports] = useState<SubstrateTsDailyReport[]>([]);
  const [substrateVsReports, setSubstrateVsReports] = useState<SubstrateVsDailyReport[]>([]);
  const [nitrateNReports,    setNitrateNReports]    = useState<NitrateNDailyReport[]>([]);
  const [phosphateReports,   setPhosphateReports]   = useState<PhosphateDailyReport[]>([]);
  const [tssLoading, setTssLoading] = useState(false);
  const [tsLoading,  setTsLoading]  = useState(false);
  const [vsLoading,  setVsLoading]  = useState(false);
  const [substrateTsLoading, setSubstrateTsLoading] = useState(false);
  const [substrateVsLoading, setSubstrateVsLoading] = useState(false);
  const [nitrateNLoading,    setNitrateNLoading]    = useState(false);
  const [phosphateLoading,   setPhosphateLoading]   = useState(false);

  const currentYear = now.getFullYear();
  const yearOptions = useMemo(
    () => Array.from({ length: 5 }, (_, i) => currentYear - i),
    [currentYear],
  );

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await labReportService.getMonthlyReport(year, month);
      setReport(data);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e?.message ?? 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const fetchTssReports = useCallback(async () => {
    setTssLoading(true);
    try {
      const prefix = `${year}-${String(month).padStart(2, '0')}`;
      const dates  = await tssService.getAvailableDates();
      const monthDates = (dates as string[]).filter((d) => d.startsWith(prefix)).sort();
      const reports = await Promise.all(monthDates.map((d) => tssService.getDailyReport(d)));
      setTssReports(reports);
    } catch {
      setTssReports([]);
    } finally {
      setTssLoading(false);
    }
  }, [year, month]);

  const fetchTsReports = useCallback(async () => {
    setTsLoading(true);
    try {
      const prefix = `${year}-${String(month).padStart(2, '0')}`;
      const dates  = await tsService.getAvailableDates();
      const monthDates = (dates as string[]).filter((d) => d.startsWith(prefix)).sort();
      const reports = await Promise.all(monthDates.map((d) => tsService.getDailyReport(d)));
      setTsReports(reports);
    } catch {
      setTsReports([]);
    } finally {
      setTsLoading(false);
    }
  }, [year, month]);

  const fetchVsReports = useCallback(async () => {
    setVsLoading(true);
    try {
      const prefix = `${year}-${String(month).padStart(2, '0')}`;
      const dates  = await vsService.getAvailableDates();
      const monthDates = (dates as string[]).filter((d) => d.startsWith(prefix)).sort();
      const reports = await Promise.all(monthDates.map((d) => vsService.getDailyReport(d)));
      setVsReports(reports);
    } catch {
      setVsReports([]);
    } finally {
      setVsLoading(false);
    }
  }, [year, month]);

  const fetchSubstrateTsReports = useCallback(async () => {
    setSubstrateTsLoading(true);
    try {
      const prefix = `${year}-${String(month).padStart(2, '0')}`;
      const dates  = await substrateTsService.getAvailableDates();
      const monthDates = (dates as string[]).filter((d) => d.startsWith(prefix)).sort();
      const reports = await Promise.all(monthDates.map((d) => substrateTsService.getDailyReport(d)));
      setSubstrateTsReports(reports);
    } catch {
      setSubstrateTsReports([]);
    } finally {
      setSubstrateTsLoading(false);
    }
  }, [year, month]);

  useEffect(() => { fetchTssReports();        }, [fetchTssReports]);
  useEffect(() => { fetchTsReports();         }, [fetchTsReports]);
  useEffect(() => { fetchVsReports();         }, [fetchVsReports]);
  useEffect(() => { fetchSubstrateTsReports(); }, [fetchSubstrateTsReports]);

  const fetchSubstrateVsReports = useCallback(async () => {
    setSubstrateVsLoading(true);
    try {
      const prefix = `${year}-${String(month).padStart(2, '0')}`;
      const dates  = await substrateVsService.getAvailableDates();
      const monthDates = (dates as string[]).filter((d) => d.startsWith(prefix)).sort();
      const reports = await Promise.all(monthDates.map((d) => substrateVsService.getDailyReport(d)));
      setSubstrateVsReports(reports);
    } catch {
      setSubstrateVsReports([]);
    } finally {
      setSubstrateVsLoading(false);
    }
  }, [year, month]);

  useEffect(() => { fetchSubstrateVsReports(); }, [fetchSubstrateVsReports]);

  const fetchNitrateNReports = useCallback(async () => {
    setNitrateNLoading(true);
    try {
      const prefix = `${year}-${String(month).padStart(2, '0')}`;
      const dates  = await nitrateNService.getAvailableDates();
      const monthDates = (dates as string[]).filter((d) => d.startsWith(prefix)).sort();
      const reports = await Promise.all(monthDates.map((d) => nitrateNService.getDailyReport(d)));
      setNitrateNReports(reports);
    } catch {
      setNitrateNReports([]);
    } finally {
      setNitrateNLoading(false);
    }
  }, [year, month]);

  useEffect(() => { fetchNitrateNReports(); }, [fetchNitrateNReports]);

  const fetchPhosphateReports = useCallback(async () => {
    setPhosphateLoading(true);
    try {
      const prefix = `${year}-${String(month).padStart(2, '0')}`;
      const dates  = await phosphateService.getAvailableDates();
      const monthDates = (dates as string[]).filter((d) => d.startsWith(prefix)).sort();
      const reports = await Promise.all(monthDates.map((d) => phosphateService.getDailyReport(d)));
      setPhosphateReports(reports);
    } catch {
      setPhosphateReports([]);
    } finally {
      setPhosphateLoading(false);
    }
  }, [year, month]);

  useEffect(() => { fetchPhosphateReports(); }, [fetchPhosphateReports]);

  const handlePrint = () => window.print();

  // ── Report view ────────────────────────────────────────────────────────────

  const renderReportView = () => {
    if (loading || tssLoading || tsLoading || vsLoading || substrateTsLoading || substrateVsLoading || nitrateNLoading || phosphateLoading) return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
    if (error) return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;

    const hasTss    = tssReports.length > 0;
    const hasTs     = tsReports.length > 0;
    const hasVs          = vsReports.length > 0;
    const hasSubstrateTs  = substrateTsReports.length > 0;
    const hasSubstrateVs  = substrateVsReports.length > 0;
    const hasNitrateN     = nitrateNReports.length > 0;
    const hasPhosphate    = phosphateReports.length > 0;
    const hasWeekly       = report != null && report.weeks.length > 0;

    if (!hasTss && !hasTs && !hasVs && !hasSubstrateTs && !hasSubstrateVs && !hasNitrateN && !hasPhosphate && !hasWeekly) return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No laboratory data recorded for {MONTHS[month - 1]} {year}. Use the Data Entry tab to add records.
      </Alert>
    );

    // ── shared table styles ───────────────────────────────────────────────────
    const TH = {
      fontWeight: 700, fontSize: '0.72rem', border: '1px solid #455a64',
      whiteSpace: 'nowrap', bgcolor: '#263238', color: '#fff', px: 1, py: 0.75,
    } as const;
    const TH2 = {
      fontWeight: 600, fontSize: '0.68rem', border: '1px solid #455a64',
      whiteSpace: 'nowrap', bgcolor: '#37474f', color: 'rgba(255,255,255,0.85)', px: 1, py: 0.5,
    } as const;
    const TD = { border: '1px solid #cfd8dc', fontSize: '0.72rem', px: 1, py: 0.5 } as const;

    const DIGEST_FERMENTERS = ['F1', 'F2', 'F3', 'IST'] as const;
    type DF = typeof DIGEST_FERMENTERS[number];
    const FERM_HDR: Record<DF, string> = { F1: '#2e7d32', F2: '#1565c0', F3: '#6a1b9a', IST: '#e65100' };
    const FERM_BG:  Record<DF, string> = { F1: '#e8f5e9', F2: '#e3f2fd', F3: '#f3e5f5', IST: '#fff3e0' };

    const fmtPpm = (v: number | null | undefined) =>
      v == null ? '—' : Number(v).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    const fmtPct = (v: number | null | undefined) =>
      v == null ? '—' : Number(v).toFixed(4);
    const remarkStyle = (remark: string | null | undefined) => {
      if (remark === 'Stable')               return { color: '#2e7d32', bgcolor: '#f1f8e9' };
      if (remark === 'Above range')          return { color: '#e65100', bgcolor: '#fff3e0' };
      if (remark === 'Below required level') return { color: '#c62828', bgcolor: '#ffebee' };
      if (remark === 'Below range')          return { color: '#c62828', bgcolor: '#ffebee' };
      if (remark === 'Beneficial')           return { color: '#1b5e20', bgcolor: '#e8f5e9' };
      if (remark === 'Moderate range')       return { color: '#e65100', bgcolor: '#fff8e1' };
      return {};
    };
    const sectionTitle = (title: string, sub: string) => (
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 1, mt: 2.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#263238' }}>{title}</Typography>
        <Typography variant="caption" sx={{ color: '#888' }}>{sub}</Typography>
      </Box>
    );
    return (
      <Box className="lab-report-print">

        {/* ── Combined analyte roll-up ── */}
        {(hasTss || hasTs || hasVs) && (() => {
          // Union of all dates across every analyte service, sorted ascending
          const allDates = Array.from(new Set([
            ...tssReports.map((r) => r.measurementDate),
            ...tsReports.map((r)  => r.measurementDate),
            ...vsReports.map((r)  => r.measurementDate),
          ])).sort();

          const getTss = (date: string, f: DF) =>
            tssReports.find((r) => r.measurementDate === date)?.fermenters.find((x) => x.fermenter === f);
          const getTs  = (date: string, f: DF) =>
            tsReports.find((r)  => r.measurementDate === date)?.fermenters.find((x) => x.fermenter === f);
          const getVs  = (date: string, f: DF) =>
            vsReports.find((r)  => r.measurementDate === date)?.fermenters.find((x) => x.fermenter === f);

          const colAvg = (vals: (number | null | undefined)[]) => {
            const ns = vals.filter((v): v is number => v != null);
            return ns.length ? ns.reduce((a, b) => a + b, 0) / ns.length : null;
          };

          // Sub-header labels — F1/F2/F3/IST use ppm; MP uses % for TS & VS
          const DIGEST_SUB = ['TSS,ppm', 'TS,ppm', 'VS,ppm', 'Nitrate-N,ppm', 'Phosphorus\n(Reactive),ppm', 'Phosphorus\n(Total),ppm'];
          const MP_SUB     = ['TS,%',    'VS,%',   'TSS,ppm', 'Nitrate-N,ppm', 'Phosphorus\n(Reactive),ppm', 'Phosphorus\n(Total),ppm'];

          return (
            <Box sx={{ mb: 3, overflowX: 'auto' }}>
              {sectionTitle('Laboratory Report', `${MONTHS[month - 1]} ${year}`)}
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small" sx={{ borderCollapse: 'collapse', minWidth: 1900 }}>
                  <TableHead>
                    {/* Row 1 — fermenter group headers */}
                    <TableRow>
                      <TableCell rowSpan={2} sx={{ ...TH, minWidth: 115, position: 'sticky', left: 0, zIndex: 3 }}>Date</TableCell>
                      {DIGEST_FERMENTERS.map((f) => (
                        <TableCell key={f} colSpan={6} align="center" sx={{ ...TH, bgcolor: FERM_HDR[f] }}>{f}</TableCell>
                      ))}
                      <TableCell colSpan={6} align="center" sx={{ ...TH, bgcolor: '#880e4f' }}>MP</TableCell>
                    </TableRow>
                    {/* Row 2 — per-analyte sub-headers */}
                    <TableRow>
                      {DIGEST_FERMENTERS.map((f) =>
                        DIGEST_SUB.map((label, i) => (
                          <TableCell key={`${f}-h${i}`} align="right"
                            sx={{ ...TH2, minWidth: 88, whiteSpace: 'pre-line' }}>{label}</TableCell>
                        ))
                      )}
                      {MP_SUB.map((label, i) => (
                        <TableCell key={`mp-h${i}`} align="right"
                          sx={{ ...TH2, minWidth: 88, whiteSpace: 'pre-line', bgcolor: '#ad1457' }}>{label}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {allDates.map((date) => (
                      <TableRow key={date} hover>
                        <TableCell sx={{ ...TD, fontWeight: 600, whiteSpace: 'nowrap', bgcolor: '#f5f5f5', position: 'sticky', left: 0, zIndex: 1 }}>
                          {date}
                        </TableCell>

                        {/* F1 / F2 / F3 / IST */}
                        {DIGEST_FERMENTERS.map((f) => {
                          const tss = getTss(date, f);
                          const ts  = getTs(date,  f);
                          const vs  = getVs(date,  f);
                          return [
                            <TableCell key={`${f}-tss`} align="right" sx={{ ...TD, bgcolor: FERM_BG[f], ...remarkStyle(tss?.remark) }}>
                              {fmtPpm(tss?.avgTssPpm)}
                            </TableCell>,
                            <TableCell key={`${f}-ts`}  align="right" sx={{ ...TD, bgcolor: FERM_BG[f], ...remarkStyle(ts?.remark) }}>
                              {fmtPpm(ts?.avgTsPpm)}
                            </TableCell>,
                            <TableCell key={`${f}-vs`}  align="right" sx={{ ...TD, bgcolor: FERM_BG[f], ...remarkStyle(vs?.remark) }}>
                              {fmtPpm(vs?.avgVsPpm)}
                            </TableCell>,
                            <TableCell key={`${f}-no3`} align="right" sx={{ ...TD, bgcolor: FERM_BG[f] }}>—</TableCell>,
                            <TableCell key={`${f}-pr`}  align="right" sx={{ ...TD, bgcolor: FERM_BG[f] }}>—</TableCell>,
                            <TableCell key={`${f}-pt`}  align="right" sx={{ ...TD, bgcolor: FERM_BG[f] }}>—</TableCell>,
                          ];
                        })}

                        {/* MP — placeholder until MP entry forms are built */}
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                          <TableCell key={`mp-${i}`} align="right" sx={{ ...TD, bgcolor: '#fce4ec' }}>—</TableCell>
                        ))}
                      </TableRow>
                    ))}

                    {/* Monthly average row */}
                    {allDates.length > 1 && (
                      <TableRow sx={{ bgcolor: '#eceff1' }}>
                        <TableCell sx={{ ...TD, fontWeight: 700, fontSize: '0.7rem', color: '#455a64', position: 'sticky', left: 0 }}>
                          Monthly Avg
                        </TableCell>
                        {DIGEST_FERMENTERS.map((f) => {
                          const avgTss = colAvg(tssReports.map((r) => r.fermenters.find((x) => x.fermenter === f)?.avgTssPpm));
                          const avgTs  = colAvg(tsReports.map((r)  => r.fermenters.find((x) => x.fermenter === f)?.avgTsPpm));
                          const avgVs  = colAvg(vsReports.map((r)  => r.fermenters.find((x) => x.fermenter === f)?.avgVsPpm));
                          return [
                            <TableCell key={`${f}-tss-a`} align="right" sx={{ ...TD, fontWeight: 700, bgcolor: FERM_BG[f], color: FERM_HDR[f] }}>{fmtPpm(avgTss)}</TableCell>,
                            <TableCell key={`${f}-ts-a`}  align="right" sx={{ ...TD, fontWeight: 700, bgcolor: FERM_BG[f], color: FERM_HDR[f] }}>{fmtPpm(avgTs)}</TableCell>,
                            <TableCell key={`${f}-vs-a`}  align="right" sx={{ ...TD, fontWeight: 700, bgcolor: FERM_BG[f], color: FERM_HDR[f] }}>{fmtPpm(avgVs)}</TableCell>,
                            <TableCell key={`${f}-no3-a`} align="right" sx={{ ...TD, bgcolor: FERM_BG[f] }}>—</TableCell>,
                            <TableCell key={`${f}-pr-a`}  align="right" sx={{ ...TD, bgcolor: FERM_BG[f] }}>—</TableCell>,
                            <TableCell key={`${f}-pt-a`}  align="right" sx={{ ...TD, bgcolor: FERM_BG[f] }}>—</TableCell>,
                          ];
                        })}
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                          <TableCell key={`mp-a${i}`} align="right" sx={{ ...TD, bgcolor: '#fce4ec' }}>—</TableCell>
                        ))}
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          );
        })()}

        {/* ── Substrate %TS section ── */}
        {hasSubstrateTs && substrateTsReports.map((rep) =>
          rep.substrates.map((sub) => (
            <Box key={`${rep.measurementDate}-${sub.substrateName}`} sx={{ mb: 3 }}>
              {sectionTitle(`${sub.substrateName} — Substrate %TS`, `${rep.measurementDate}  ·  Target: 2.5 – 12 %`)}
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#1b5e20' }}>
                      {['Replicate', 'Dish Wt (g)', 'Sample Wt (g)', 'Sample+Dish (g)', 'Constant Wt (g)', '%TS'].map((h) => (
                        <TableCell key={h} align="center" sx={{ color: '#fff', fontWeight: 700, border: '1px solid #388e3c' }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sub.replicates.map((r) => (
                      <TableRow key={r.replicate} hover>
                        <TableCell align="center" sx={{ fontWeight: 600, border: '1px solid #c8e6c9' }}>{r.replicate}</TableCell>
                        <TableCell align="center" sx={{ border: '1px solid #c8e6c9' }}>{r.dishWeightG != null ? Number(r.dishWeightG).toFixed(4) : '—'}</TableCell>
                        <TableCell align="center" sx={{ border: '1px solid #c8e6c9' }}>{r.sampleWeightG != null ? Number(r.sampleWeightG).toFixed(4) : '—'}</TableCell>
                        <TableCell align="center" sx={{ border: '1px solid #c8e6c9' }}>{r.samplePlusDishWeightG != null ? Number(r.samplePlusDishWeightG).toFixed(4) : '—'}</TableCell>
                        <TableCell align="center" sx={{ border: '1px solid #c8e6c9' }}>{r.constantWeightG != null ? Number(r.constantWeightG).toFixed(4) : '—'}</TableCell>
                        <TableCell align="center" sx={{ border: '1px solid #c8e6c9' }}>{r.pctTs != null ? Number(r.pctTs).toFixed(4) : '—'}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: '#e8f5e9' }}>
                      <TableCell align="center" colSpan={5} sx={{ fontWeight: 700, border: '1px solid #c8e6c9' }}>Average</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, border: '1px solid #c8e6c9' }}>
                        {sub.avgPctTs != null ? Number(sub.avgPctTs).toFixed(4) : '—'}
                        {sub.remark && (
                          <Chip
                            label={sub.remark}
                            size="small"
                            sx={{
                              ml: 1,
                              ...remarkStyle(sub.remark as any),
                            }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ))
        )}

        {/* ── Substrate %VS section ── */}
        {hasSubstrateVs && substrateVsReports.map((rep) =>
          rep.substrates.map((sub) => (
            <Box key={`${rep.measurementDate}-${sub.substrateName}-vs`} sx={{ mb: 3 }}>
              {sectionTitle(`${sub.substrateName} — Substrate %VS`, `${rep.measurementDate}  ·  Target: 70 – 90 %`)}
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#4a148c' }}>
                      {['Replicate', 'Dish Wt (g)', 'Dried+Dish (g)', 'Residue+Dish (g)', '%VS'].map((h) => (
                        <TableCell key={h} align="center" sx={{ color: '#fff', fontWeight: 700, border: '1px solid #6a1b9a' }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sub.replicates.map((r) => (
                      <TableRow key={r.replicate} hover>
                        <TableCell align="center" sx={{ fontWeight: 600, border: '1px solid #e1bee7' }}>{r.replicate}</TableCell>
                        <TableCell align="center" sx={{ border: '1px solid #e1bee7' }}>{r.dishWeightG != null ? Number(r.dishWeightG).toFixed(4) : '—'}</TableCell>
                        <TableCell align="center" sx={{ border: '1px solid #e1bee7' }}>{r.driedSamplePlusDishG != null ? Number(r.driedSamplePlusDishG).toFixed(4) : '—'}</TableCell>
                        <TableCell align="center" sx={{ border: '1px solid #e1bee7' }}>{r.residuePlusDishG != null ? Number(r.residuePlusDishG).toFixed(4) : '—'}</TableCell>
                        <TableCell align="center" sx={{ border: '1px solid #e1bee7' }}>{r.pctVs != null ? Number(r.pctVs).toFixed(4) : '—'}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: '#f3e5f5' }}>
                      <TableCell align="center" colSpan={4} sx={{ fontWeight: 700, border: '1px solid #e1bee7' }}>Average</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, border: '1px solid #e1bee7' }}>
                        {sub.avgPctVs != null ? Number(sub.avgPctVs).toFixed(4) : '—'}
                        {sub.remark && (
                          <Chip label={sub.remark} size="small" sx={{ ml: 1, ...remarkStyle(sub.remark as any) }} />
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ))
        )}

        {/* ── Nitrate-N section ── */}
        {hasNitrateN && (() => {
          const NITRATE_FERMENTERS = ['F1', 'F2', 'F3', 'IST', 'MIXING PIT'] as const;
          const allDates = nitrateNReports.map((r) => r.measurementDate).sort();
          const getRow = (date: string, ferm: string) =>
            nitrateNReports.find((r) => r.measurementDate === date)
              ?.fermenters.find((f) => f.fermenter === ferm);

          // Build flat rows: one per (date × fermenter) — only include fermenters with data
          const flatRows: { date: string; ferm: string; isFirstOfDate: boolean; dateRowSpan: number }[] = [];
          for (const date of allDates) {
            const report = nitrateNReports.find((r) => r.measurementDate === date);
            const activeFerms = NITRATE_FERMENTERS.filter((f) =>
              report?.fermenters.find((fe) => fe.fermenter === f && fe.ppmNo3 != null),
            );
            // If no active fermenters, show all rows so user sees the date
            const fermsToShow = activeFerms.length > 0 ? activeFerms : [...NITRATE_FERMENTERS];
            fermsToShow.forEach((ferm, i) => {
              flatRows.push({ date, ferm, isFirstOfDate: i === 0, dateRowSpan: fermsToShow.length });
            });
          }
          const totalRows = flatRows.length;

          const TH = { color: '#fff', fontWeight: 700, border: '1px solid #2e7d32', fontSize: '0.75rem', px: 1.5, py: 1, whiteSpace: 'nowrap' as const };
          const TH2 = { color: '#fff', fontWeight: 600, border: '1px solid #388e3c', fontSize: '0.7rem', px: 1.5, py: 0.75, whiteSpace: 'nowrap' as const };
          const TD = { border: '1px solid #c8e6c9', fontSize: '0.75rem', px: 1.5, py: 0.6 };

          return (
            <Box sx={{ mb: 3, width: '100%' }}>
              {sectionTitle('Nitrate-Nitrogen (NO₃-N)', `${MONTHS[month - 1]} ${year}`)}
              <Box sx={{ overflowX: 'auto', width: '100%' }}>
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, width: '100%' }}>
                  <Table size="small" sx={{ borderCollapse: 'collapse', width: '100%' }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#1b5e20' }}>
                        <TableCell align="center" rowSpan={2} sx={{ ...TH, minWidth: 110, verticalAlign: 'middle' }}>Date</TableCell>
                        <TableCell align="center" rowSpan={2} sx={{ ...TH, minWidth: 120, verticalAlign: 'middle' }}>Fermenter</TableCell>
                        <TableCell align="center" sx={{ ...TH }}>ppm, NO₃⁻</TableCell>
                        <TableCell align="center" sx={{ ...TH }}>ppm NO₃-N</TableCell>
                        <TableCell align="center" rowSpan={2} sx={{ ...TH, minWidth: 120, verticalAlign: 'middle', bgcolor: '#2e7d32' }}>Target Range</TableCell>
                        <TableCell align="center" rowSpan={2} sx={{ ...TH, minWidth: 110, verticalAlign: 'middle' }}>Remarks</TableCell>
                      </TableRow>
                      <TableRow sx={{ bgcolor: '#2e7d32' }}>
                        <TableCell align="center" sx={{ ...TH2, fontStyle: 'italic' }}>ppm</TableCell>
                        <TableCell align="center" sx={{ ...TH2, fontStyle: 'italic' }}>ppm</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {flatRows.map((row, rowIdx) => {
                        const data = getRow(row.date, row.ferm);
                        const rs   = remarkStyle(data?.remark);
                        const isFirst = rowIdx === 0;
                        return (
                          <TableRow key={`${row.date}-${row.ferm}`} hover>
                            {/* Date cell — merged per date group */}
                            {row.isFirstOfDate && (
                              <TableCell
                                align="center"
                                rowSpan={row.dateRowSpan}
                                sx={{ ...TD, fontWeight: 700, whiteSpace: 'nowrap', verticalAlign: 'middle', bgcolor: '#f1f8e9' }}
                              >
                                {row.date}
                              </TableCell>
                            )}
                            <TableCell align="center" sx={{ ...TD, fontWeight: 600 }}>{row.ferm}</TableCell>
                            <TableCell align="center" sx={TD}>
                              {data?.ppmNo3 != null ? Number(data.ppmNo3).toFixed(2) : '—'}
                            </TableCell>
                            <TableCell align="center" sx={{ ...TD, ...rs, fontWeight: data?.remark ? 600 : 400 }}>
                              {data?.ppmNo3N != null ? Number(data.ppmNo3N).toFixed(2) : '—'}
                            </TableCell>
                            {/* Target Range — single merged cell spanning all body rows */}
                            {isFirst && (
                              <TableCell
                                align="center"
                                rowSpan={totalRows}
                                sx={{
                                  ...TD,
                                  verticalAlign: 'middle',
                                  bgcolor: '#e8f5e9',
                                  fontWeight: 700,
                                  color: '#1b5e20',
                                  fontSize: '0.8rem',
                                  lineHeight: 1.8,
                                  borderLeft: '2px solid #81c784',
                                }}
                              >
                                <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#1b5e20' }}>NO₃-N</Typography>
                                <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#c62828' }}>&lt;50 mg/L</Typography>
                              </TableCell>
                            )}
                            <TableCell align="center" sx={{ ...TD, ...rs, fontWeight: data?.remark ? 600 : 400 }}>
                              {data?.remark ?? '—'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              {/* Legend */}
              <Box sx={{ mt: 1, p: 1.5, bgcolor: '#f9fbe7', borderRadius: 2, border: '1px solid #dce775' }}>
                <Typography variant="caption" sx={{ color: '#555', fontWeight: 700, display: 'block', mb: 0.5 }}>Interpretation:</Typography>
                <Typography variant="caption" sx={{ color: '#1b5e20', display: 'block' }}>
                  <strong>Beneficial (&lt;50 mg/L):</strong> "Safe, may be assimilated by microbes, small amounts can be tolerated."
                </Typography>
                <Typography variant="caption" sx={{ color: '#c62828', display: 'block' }}>
                  <strong>Above range (200–500 mg/L):</strong> "Strong inhibition of methane production, as nitrate and nitrite toxicity affect."
                </Typography>
                <Typography variant="caption" sx={{ color: '#e65100', display: 'block' }}>
                  <strong>Moderate range (50–200 mg/L):</strong> "Can start competing with methanogenesis and reducing methane yield."
                </Typography>
              </Box>
            </Box>
          );
        })()}

        {/* ── Phosphate section ── */}
        {hasPhosphate && (() => {
          const PHOS_FERMENTERS = ['F1', 'F2', 'F3', 'IST', 'MIXING PIT'] as const;
          const allDates = phosphateReports.map((r) => r.measurementDate).sort();
          const getRow = (date: string, ferm: string) =>
            phosphateReports.find((r) => r.measurementDate === date)
              ?.fermenters.find((f) => f.fermenter === ferm);

          // Build flat rows: one per (date × fermenter) with data
          const flatRows: { date: string; ferm: string; isFirstOfDate: boolean; dateRowSpan: number }[] = [];
          for (const date of allDates) {
            const report = phosphateReports.find((r) => r.measurementDate === date);
            const activeFerms = PHOS_FERMENTERS.filter((f) =>
              report?.fermenters.find((fe) => fe.fermenter === f && (fe.reactivePpm != null || fe.totalPpm != null)),
            );
            const fermsToShow = activeFerms.length > 0 ? activeFerms : [...PHOS_FERMENTERS];
            fermsToShow.forEach((ferm, i) => {
              flatRows.push({ date, ferm, isFirstOfDate: i === 0, dateRowSpan: fermsToShow.length });
            });
          }
          const totalRows = flatRows.length;

          const PTH  = (bg = '#880e4f') => ({ color: '#fff', fontWeight: 700, border: `1px solid rgba(255,255,255,0.25)`, fontSize: '0.73rem', px: 1.5, py: 1, whiteSpace: 'nowrap' as const, bgcolor: bg });
          const PTD  = { border: '1px solid #fce4ec', fontSize: '0.73rem', px: 1.5, py: 0.6 };

          const phosphateRemarkStyle = (remark: string | null | undefined) => {
            if (remark === 'Beneficial')  return { color: '#1b5e20', bgcolor: '#e8f5e9' };
            if (remark === 'Below range') return { color: '#1565c0', bgcolor: '#e3f2fd' };
            if (remark === 'Above range') return { color: '#c62828', bgcolor: '#ffebee' };
            return {};
          };

          return (
            <Box sx={{ mb: 3, width: '100%' }}>
              {sectionTitle('Phosphate as Phosphorus — Reactive &amp; Total', `${MONTHS[month - 1]} ${year}`)}
              <Box sx={{ overflowX: 'auto', width: '100%' }}>
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, width: '100%' }}>
                  <Table size="small" sx={{ borderCollapse: 'collapse', width: '100%' }}>
                    <TableHead>
                      {/* Header row 1 — group labels */}
                      <TableRow>
                        <TableCell align="center" rowSpan={2} sx={{ ...PTH(), minWidth: 110, verticalAlign: 'middle' }}>Date</TableCell>
                        <TableCell align="center" rowSpan={2} sx={{ ...PTH(), minWidth: 120, verticalAlign: 'middle' }}>Fermenter</TableCell>
                        <TableCell align="center" colSpan={4} sx={{ ...PTH('#880e4f') }}>Phosphate as Phosphorus (Reactive)</TableCell>
                        <TableCell align="center" colSpan={4} sx={{ ...PTH('#e65100') }}>Phosphate as Phosphorus (Total)</TableCell>
                      </TableRow>
                      {/* Header row 2 — column sub-labels */}
                      <TableRow sx={{ bgcolor: '#ad1457' }}>
                        <TableCell align="center" sx={{ ...PTH('#ad1457') }}>ppm, PO₄³⁻-P</TableCell>
                        <TableCell align="center" sx={{ ...PTH('#ad1457') }}>ppm, PO₄³⁻-P (mg/L)</TableCell>
                        <TableCell align="center" sx={{ ...PTH('#c2185b') }}>Target Range</TableCell>
                        <TableCell align="center" sx={{ ...PTH('#c2185b') }}>Remarks</TableCell>
                        <TableCell align="center" sx={{ ...PTH('#f57c00') }}>ppm, PO₄³⁻</TableCell>
                        <TableCell align="center" sx={{ ...PTH('#f57c00') }}>ppm, PO₄³⁻ (mg/L)</TableCell>
                        <TableCell align="center" sx={{ ...PTH('#e65100') }}>Target Range</TableCell>
                        <TableCell align="center" sx={{ ...PTH('#e65100') }}>Remarks</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {flatRows.map((row, rowIdx) => {
                        const data = getRow(row.date, row.ferm);
                        const isFirst = rowIdx === 0;
                        return (
                          <TableRow key={`${row.date}-${row.ferm}`} hover>
                            {row.isFirstOfDate && (
                              <TableCell align="center" rowSpan={row.dateRowSpan}
                                sx={{ ...PTD, fontWeight: 700, whiteSpace: 'nowrap', verticalAlign: 'middle', bgcolor: '#fce4ec' }}>
                                {row.date}
                              </TableCell>
                            )}
                            <TableCell align="center" sx={{ ...PTD, fontWeight: 600 }}>{row.ferm}</TableCell>
                            {/* Reactive */}
                            <TableCell align="center" sx={PTD}>
                              {data?.reactivePpm != null ? Number(data.reactivePpm).toFixed(2) : '—'}
                            </TableCell>
                            <TableCell align="center" sx={{ ...PTD, fontWeight: data?.reactiveMgL != null ? 700 : 400, color: '#880e4f', bgcolor: data?.reactiveMgL != null ? '#fce4ec' : undefined }}>
                              {data?.reactiveMgL != null ? Number(data.reactiveMgL).toFixed(0) : '—'}
                            </TableCell>
                            {/* Reactive Target Range — merged across all rows */}
                            {isFirst && (
                              <TableCell align="center" rowSpan={totalRows}
                                sx={{ ...PTD, verticalAlign: 'middle', bgcolor: '#fce4ec', borderLeft: '2px solid #f48fb1' }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#880e4f' }}>PO₄³⁻-P</Typography>
                                <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#c62828' }}>10-200 mg/L</Typography>
                              </TableCell>
                            )}
                            <TableCell align="center" sx={{ ...PTD, ...phosphateRemarkStyle(data?.reactiveRemark), fontWeight: data?.reactiveRemark ? 600 : 400 }}>
                              {data?.reactiveRemark ?? '—'}
                            </TableCell>
                            {/* Total */}
                            <TableCell align="center" sx={PTD}>
                              {data?.totalPpm != null ? Number(data.totalPpm).toFixed(2) : '—'}
                            </TableCell>
                            <TableCell align="center" sx={{ ...PTD, fontWeight: data?.totalMgL != null ? 700 : 400, color: '#e65100', bgcolor: data?.totalMgL != null ? '#fff3e0' : undefined }}>
                              {data?.totalMgL != null ? Number(data.totalMgL).toFixed(0) : '—'}
                            </TableCell>
                            {/* Total Target Range — merged across all rows */}
                            {isFirst && (
                              <TableCell align="center" rowSpan={totalRows}
                                sx={{ ...PTD, verticalAlign: 'middle', bgcolor: '#fff3e0', borderLeft: '2px solid #ffcc80' }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#e65100' }}>Total Phosphorus</Typography>
                                <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#e65100' }}>100-1000 mg/L</Typography>
                              </TableCell>
                            )}
                            <TableCell align="center" sx={{ ...PTD, ...phosphateRemarkStyle(data?.totalRemark), fontWeight: data?.totalRemark ? 600 : 400 }}>
                              {data?.totalRemark ?? '—'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              {/* Legend */}
              <Box sx={{ mt: 1, p: 1.5, bgcolor: '#fce4ec', borderRadius: 2, border: '1px solid #f48fb1' }}>
                <Typography variant="caption" sx={{ color: '#880e4f', fontWeight: 700, display: 'block', mb: 0.5 }}>Interpretation:</Typography>
                <Typography variant="caption" sx={{ color: '#1b5e20', display: 'block' }}>
                  <strong>Beneficial:</strong> "Supports healthy microbial activity and stable biogas production."
                </Typography>
                <Typography variant="caption" sx={{ color: '#c62828', display: 'block' }}>
                  <strong>Above range:</strong> "Can precipitate with magnesium and ammonium, causing scaling in pipes and reactors."
                </Typography>
                <Typography variant="caption" sx={{ color: '#1565c0', display: 'block' }}>
                  <strong>Below range:</strong> "Can limit microbial growth, leading to reduced methane yield."
                </Typography>
              </Box>
            </Box>
          );
        })()}

        {/* ── Weekly summary (legacy entries) ── */}
        {hasWeekly && (
        <Box sx={{ overflowX: 'auto' }}>
          {sectionTitle('Weekly Summary', `${MONTHS[month - 1]} ${year}`)}
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small" sx={{ minWidth: 1600, borderCollapse: 'collapse' }}>
            {/* ── HEADER ROW 1 – section colour blocks ── */}
            <TableHead>
              <TableRow>
                {/* Week label column */}
                <TableCell
                  rowSpan={3}
                  align="center"
                  sx={{
                    fontWeight: 700,
                    border: '1px solid #bbb',
                    bgcolor: '#37474f',
                    color: '#fff',
                    whiteSpace: 'nowrap',
                    minWidth: 160,
                    fontSize: '0.75rem',
                  }}
                >
                  WEEK
                </TableCell>

                {SECTIONS.map((sec) => {
                  const { header } = SECTION_COLORS[sec];
                  const cols = colsFor(sec);
                  return (
                    <TableCell
                      key={sec}
                      colSpan={cols.length}
                      align="center"
                      sx={{
                        fontWeight: 700,
                        border: '1px solid #bbb',
                        bgcolor: header,
                        color: '#fff',
                        fontSize: '0.8rem',
                        letterSpacing: 1,
                      }}
                    >
                      {sec}
                    </TableCell>
                  );
                })}
              </TableRow>

              {/* ── HEADER ROW 2 – Parameter names ── */}
              <TableRow>
                {SECTIONS.map((sec) =>
                  colsFor(sec).map((col) => {
                    const { subheader } = SECTION_COLORS[sec];
                    return (
                      <TableCell
                        key={`${sec}-${col.key}`}
                        align="center"
                        sx={{
                          fontWeight: 600,
                          border: '1px solid #bbb',
                          bgcolor: subheader,
                          color: '#fff',
                          whiteSpace: 'nowrap',
                          fontSize: '0.68rem',
                          px: 0.5,
                        }}
                      >
                        {col.label}
                      </TableCell>
                    );
                  }),
                )}
              </TableRow>

              {/* ── HEADER ROW 3 – Unit ── */}
              <TableRow>
                {SECTIONS.map((sec) =>
                  colsFor(sec).map((col) => {
                    const { subheader } = SECTION_COLORS[sec];
                    return (
                      <TableCell
                        key={`${sec}-${col.key}-unit`}
                        align="center"
                        sx={{
                          fontWeight: 400,
                          border: '1px solid #bbb',
                          bgcolor: subheader,
                          color: 'rgba(255,255,255,0.85)',
                          fontSize: '0.62rem',
                          px: 0.5,
                          fontStyle: 'italic',
                        }}
                      >
                        ({col.unit})
                      </TableCell>
                    );
                  }),
                )}
              </TableRow>
            </TableHead>

            {/* ── DATA ROWS ── */}
            <TableBody>
            {report!.weeks.map((row: WeeklyLabRow) => (
                <TableRow key={row.weekOfMonth} hover>
                  {/* Week label */}
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      border: '1px solid #ddd',
                      whiteSpace: 'nowrap',
                      fontSize: '0.72rem',
                      bgcolor: '#f5f5f5',
                    }}
                  >
                    {row.weekLabel}
                  </TableCell>

                  {SECTIONS.map((sec) => {
                    const entry: LabReportEntry | null = row.sections[sec];
                    const { cellBg } = SECTION_COLORS[sec];
                    return colsFor(sec).map((col) => {
                      const val = entry ? (entry[col.key] as number | null | undefined) : null;
                      return (
                        <TableCell
                          key={`${sec}-${col.key}`}
                          align="center"
                          sx={{
                            border: '1px solid #ddd',
                            bgcolor: entry ? cellBg : '#fafafa',
                            fontSize: '0.72rem',
                            color: val == null ? '#bbb' : 'inherit',
                          }}
                        >
                          {fmt(val)}
                        </TableCell>
                      );
                    });
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Legend */}
        <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {SECTIONS.map((sec) => (
            <Box key={sec} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: 0.5,
                  bgcolor: SECTION_COLORS[sec].header,
                }}
              />
              <Typography variant="caption" sx={{ color: '#555', fontWeight: 500 }}>
                {sec}
                {sec === 'MP' ? ' – Manure Pit' : sec === 'IST' ? ' – Inlet Storage Tank' : sec === 'F1' ? ' – Fermenter 1' : sec === 'F2' ? ' – Fermenter 2' : ' – Fermenter 3'}
              </Typography>
            </Box>
          ))}
        </Box>
        </Box>
        )}

      </Box>
    );
  };

  // ── Data entry cards ───────────────────────────────────────────────────────

  const DATA_ENTRY_ITEMS = [
    {
      key: 'tss',
      label: 'Total Suspended Solids',
      abbr: 'TSS',
      desc: 'Enter tri-replicate filter disc & contant weights. Auto-calculates TSS (ppm), Average, Remarks, %TSS.',
      route: '/lab-report/tss',
      color: '#37474f',
      ready: true,
    },
    { key: 'ts',  abbr: 'TS',         label: 'Total Solids',          desc: 'Gravimetric TS determination per section.', route: '/lab-report/ts', color: '#1565c0', ready: true  },
    { key: 'vs',  abbr: 'VS',         label: 'Volatile Solids',       desc: 'Loss on ignition at 550 °C.', route: '/lab-report/vs', color: '#4a148c', ready: true  },
    { key: 'substrate-ts', abbr: 'Sub %TS', label: 'Substrate Total Solids', desc: 'Percentage TS for substrate (e.g. POLOMOLOK). Target: 2.5 – 12%.', route: '/lab-report/substrate-ts', color: '#1b5e20', ready: true },
    { key: 'substrate-vs', abbr: 'Sub %VS', label: 'Substrate Volatile Solids', desc: 'Percentage VS for substrate (e.g. POLOMOLOK). Target: 70 – 90%.', route: '/lab-report/substrate-vs', color: '#4a148c', ready: true },
    { key: 'no3', abbr: 'Nitrate-N',  label: 'Nitrate Nitrogen',      desc: 'ppm NO₃-N per fermenter. Formula: NO₃⁻ × 0.226. Target: <50 mg/L.', route: '/lab-report/nitrate-n', color: '#1b5e20', ready: true },
    { key: 'pr',  abbr: 'Phos. React.', label: 'Phosphorus Reactive', desc: 'Reactive phosphorus (PO₄³⁻-P). Formula: ppm × 200. Target: 10–200 mg/L.', route: '/lab-report/phosphate?mode=reactive', color: '#880e4f', ready: true },
    { key: 'pt',  abbr: 'Phos. Total', label: 'Phosphorus Total',     desc: 'Total phosphorus (PO₄³⁻). Formula: ppm × 200. Target: 100–1000 mg/L.', route: '/lab-report/phosphate?mode=total', color: '#e65100', ready: true },
  ];

  const renderDataEntry = () => (
    <Box>
      <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
        Select a parameter to open its data entry form. Entries are stored at the replicate level and aggregated into the consolidated Report View above.
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
        {DATA_ENTRY_ITEMS.map((item) => (
          <Card
            key={item.key}
            variant="outlined"
            sx={{
              borderRadius: 2,
              opacity: item.ready ? 1 : 0.55,
              borderColor: item.ready ? item.color : '#ccc',
            }}
          >
            <CardActionArea
              onClick={() => item.ready && navigate(item.route)}
              disabled={!item.ready}
              sx={{ height: '100%' }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <ScienceIcon sx={{ color: item.color, fontSize: '1.4rem' }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: item.color }}>
                    {item.abbr}
                  </Typography>
                  {!item.ready && (
                    <Typography variant="caption" sx={{ ml: 'auto', color: '#aaa' }}>
                      Coming soon
                    </Typography>
                  )}
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {item.label}
                </Typography>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  {item.desc}
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Box>
    </Box>
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      {/* ── Page header ── */}
      <Paper
        className="no-print"
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          background: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 60%, #388e3c 100%)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <BiotechIcon sx={{ fontSize: '2.2rem', opacity: 0.9 }} />
          <Box>
            <Typography variant="overline" sx={{ opacity: 0.8, display: 'block', lineHeight: 1, letterSpacing: 2 }}>
              POLOMOLOK PLANT
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Chemical Laboratory Report
            </Typography>
          </Box>
        </Box>

        {/* Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel sx={{ color: 'rgba(255,255,255,0.8)' }}>Month</InputLabel>
            <Select
              value={month}
              label="Month"
              onChange={(e) => setMonth(Number(e.target.value))}
              sx={{ color: '#fff', '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' } }}
            >
              {MONTHS.map((m, i) => (
                <MenuItem key={i + 1} value={i + 1}>{m}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 90 }}>
            <InputLabel sx={{ color: 'rgba(255,255,255,0.8)' }}>Year</InputLabel>
            <Select
              value={year}
              label="Year"
              onChange={(e) => setYear(Number(e.target.value))}
              sx={{ color: '#fff', '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' } }}
            >
              {yearOptions.map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Tooltip title="Print report">
            <IconButton onClick={handlePrint} sx={{ color: '#fff' }}>
              <PrintIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* ── Print header (only visible when printing) ── */}
      <Box className="print-only" sx={{ display: 'none', mb: 2, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>POLOMOLOK PLANT</Typography>
        <Typography variant="h6">CHEMICAL LABORATORY REPORT</Typography>
        <Typography variant="body2">{MONTHS[month - 1]} {year}</Typography>
        <Divider sx={{ my: 1 }} />
      </Box>

      {/* ── Tabs ── */}
      <Box className="no-print" sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} textColor="primary" indicatorColor="primary">
          <Tab label="Report View" />
          <Tab label="Data Entry" />
        </Tabs>
      </Box>

      {tab === 0 ? renderReportView() : renderDataEntry()}

      {/* Print styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          .lab-report-print { font-size: 10px; }
          body { background: #fff !important; }
        }
      `}</style>
    </Box>
  );
};

export default ChemicalLabReport;
